import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { computeOverallScore, scoreToGrade, computeCUARank, CHALLENGE_TIERS } from './Evaluator';
import { CHALLENGES, DIFFICULTY_COLOR, CATEGORY_ICONS } from '../challengeRegistry';
import { saveRun } from '../agentAPI';
import AgentInsights from './AgentInsights';

// Speed score: 100 pts if <= 20s, linear decay to 0 at 180s
const calcSpeedScore = (t) => t == null ? null : Math.max(0, Math.round(100 - ((Math.max(0, t - 20) / 160) * 100)));
// Efficiency score: based on error attempts (0 errors = 100, each error -20, min 0)
const calcEfficiencyScore = (errors) => errors == null ? null : Math.max(0, 100 - errors * 20);
// Smartness: weighted composite — 50% accuracy + 30% speed + 20% efficiency
const calcSmartness = (accuracy, speed, efficiency) => {
  if (speed == null && efficiency == null) return null;
  const s = speed ?? 50; const e = efficiency ?? 50;
  return Math.round(accuracy * 0.5 + s * 0.3 + e * 0.2);
};

export default function ResultsDashboard({ results, onReset, onRetry, agent, evalStartTime, totalTime: totalTimeProp }) {
  const [expanded, setExpanded] = useState(null);
  const overall = computeOverallScore(results, CHALLENGES);

  const [runSaved, setRunSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const attempted = results.length;
  const passed = results.filter(r => r.passed).length;
  const partial = results.filter(r => !r.passed && r.score > 0).length;
  const failed = results.filter(r => r.score === 0).length;
  const totalTime = results.reduce((sum, r) => sum + (r.timeSpent || 0), 0);
  const formatTime = (s) => s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
  const avgTime = attempted > 0 ? Math.round(totalTime / attempted) : 0;

  const resultsWithMetrics = results.map(r => ({
    ...r,
    speedScore: calcSpeedScore(r.timeSpent),
    efficiencyScore: calcEfficiencyScore(r.errorAttempts),
    smartnessScore: calcSmartness(
      r.score,
      calcSpeedScore(r.timeSpent),
      calcEfficiencyScore(r.errorAttempts)
    ),
  }));

  const hasMetrics = resultsWithMetrics.some(r => r.speedScore != null);
  const speedItems = resultsWithMetrics.filter(r => r.speedScore != null);
  const effItems = resultsWithMetrics.filter(r => r.efficiencyScore != null);
  const avgSpeed = speedItems.length > 0 ? Math.round(speedItems.reduce((s, r) => s + r.speedScore, 0) / speedItems.length) : null;
  const avgEfficiency = effItems.length > 0 ? Math.round(effItems.reduce((s, r) => s + r.efficiencyScore, 0) / effItems.length) : null;
  const avgSmartness = calcSmartness(overall.pct ?? 0, avgSpeed, avgEfficiency);

  const handleSaveRun = async () => {
    if (!agent) { setSaveError('Login to save your run to the leaderboard.'); return; }
    try {
      await saveRun({
        agentId:        agent.agentId,
        agentName:      agent.name,
        model:          agent.model,
        startedAt:      evalStartTime || new Date().toISOString(),
        totalTime:      totalTimeProp || totalTime,
        scorePct:       overall.pct ?? 0,
        rawScore:       overall.raw ?? 0,
        totalPoints:    overall.total ?? 0,
        passed,
        attempted,
        speedScore:     avgSpeed  ?? 0,
        efficiencyScore:avgEfficiency ?? 0,
        smartnessScore: avgSmartness  ?? 0,
        challenges:     results.map(r => ({ challengeId: r.challengeId, score: r.score, timeSpent: r.timeSpent })),
      });
      setRunSaved(true);
      setSaveError('');
    } catch(e) { setSaveError('Failed to save run: ' + (e.message || 'Unknown error')); }
  };

  const cuaRank = computeCUARank(avgSmartness ?? overall.pct ?? 0);

  const byTier = ['Foundational', 'Intermediate', 'Advanced', 'Elite'].map(tierName => {
    const tierChallenges = CHALLENGES.filter(ch => ch.tier === tierName);
    const tierResults = tierChallenges.map(ch => results.find(r => r.challengeId === ch.id)).filter(Boolean);
    const totalPts = tierChallenges.reduce((s, ch) => s + ch.points, 0);
    const earnedPts = tierResults.reduce((s, r) => {
      const ch = tierChallenges.find(c => c.id === r.challengeId);
      return s + (ch ? Math.round((r.score / 100) * ch.points) : 0);
    }, 0);
    const pct = totalPts > 0 ? Math.round((earnedPts / totalPts) * 100) : 0;
    const tierStyle = Object.values(CHALLENGE_TIERS).find(t => t.tier === tierName) || {};
    return { tierName, total: tierChallenges.length, attempted: tierResults.length, totalPts, earnedPts, pct, color: tierStyle.color, bg: tierStyle.bg };
  });

  const byCategory = CHALLENGES.reduce((acc, ch) => {
    if (!acc[ch.category]) acc[ch.category] = { total: 0, earned: 0, count: 0 };
    const r = results.find(r => r.challengeId === ch.id);
    acc[ch.category].count++;
    acc[ch.category].total += ch.points;
    if (r) acc[ch.category].earned += Math.round((r.score / 100) * ch.points);
    return acc;
  }, {});

  const smart = avgSmartness ?? overall.pct ?? 0;
  const RANKS = [
    { rank:'Unranked', min:0, icon:'◌', cls:'unranked' },
    { rank:'Bronze', min:20, icon:'🥉', cls:'bronze' },
    { rank:'Silver', min:35, icon:'🥈', cls:'silver' },
    { rank:'Gold', min:50, icon:'🥇', cls:'gold' },
    { rank:'Platinum', min:65, icon:'💠', cls:'platinum' },
    { rank:'Diamond', min:75, icon:'💎', cls:'diamond' },
    { rank:'Master', min:85, icon:'⚜️', cls:'master' },
    { rank:'Grandmaster', min:95, icon:'👑', cls:'grandmaster' },
  ];

  return (
    <div className="rd">
      {/* ── ACTION BAR ──────────────────────────────────────────────── */}
      <div className="rd-bar">
        <div className="rd-bar-left">
          {agent
            ? <span className="rd-bar-agent">🤖 <strong>{agent.name}</strong> · {agent.model} · {agent.framework}</span>
            : <span className="rd-bar-guest">👤 Guest run — <Link to="/arena/auth" className="rd-link">Login to save</Link></span>
          }
        </div>
        <div className="rd-bar-right">
          {!runSaved
            ? <button className="rd-save-btn" onClick={handleSaveRun}>💾 Save to Leaderboard</button>
            : <span className="rd-saved-pill">✓ Saved to Leaderboard</span>
          }
          {runSaved && <Link to="/arena/my-runs" className="rd-lb-link">📋 My Runs</Link>}
          <Link to="/arena/leaderboard" className="rd-lb-link">🏆 Leaderboard</Link>
          {saveError && <span className="rd-error">{saveError}</span>}
        </div>
      </div>

      {/* ── SCROLLABLE BODY ─────────────────────────────────────────── */}
      <div className="rd-scroll">
        {/* ── HERO ──────────────────────────────────────────────────── */}
        <div className="rd-hero">
          <div className="rd-hero-left">
            <div className="rd-ring-wrap">
              <svg viewBox="0 0 120 120" width="120" height="120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="9" />
                <circle cx="60" cy="60" r="52" fill="none" className={`rd-ring rd-ring-${overall.grade.cls}`} strokeWidth="9"
                  strokeDasharray={`${(overall.pct / 100) * 326.7} 326.7`}
                  strokeLinecap="round" transform="rotate(-90 60 60)" />
              </svg>
              <div className="rd-ring-label">
                <span className="rd-ring-pct">{overall.pct ?? 0}</span>
                <span className="rd-ring-unit">%</span>
              </div>
            </div>
            <div className="rd-hero-text">
              <span className={`rd-pill rd-pill-${overall.grade.cls}`}>{overall.grade.label}</span>
              <h1 className="rd-hero-title">Evaluation Complete</h1>
              <p className="rd-hero-sub">{overall.raw ?? 0}/{overall.total ?? 0} pts · {attempted} challenges · {formatTime(totalTime)}</p>
            </div>
          </div>
          <div className={`rd-hero-rank rd-hero-rank-${cuaRank.cls}`}>
            <span className="rd-hero-rank-icon">{cuaRank.icon}</span>
            <div className="rd-hero-rank-body">
              <div className="rd-hero-rank-ey">CUA RANK</div>
              <div className="rd-hero-rank-name">{cuaRank.rank}</div>
              {cuaRank.next && (
                <div className="rd-hero-rank-prog">
                  <div className="rd-hero-rank-prog-lbl">
                    <span>→ {cuaRank.next.icon} {cuaRank.next.rank}</span>
                    <span>{cuaRank.smartness}/{cuaRank.next.min}</span>
                  </div>
                  <div className="rd-track"><div className="rd-track-fill" style={{ width: `${cuaRank.progressPct}%` }} /></div>
                </div>
              )}
              {!cuaRank.next && <div className="rd-hero-rank-max">👑 Maximum Rank Achieved</div>}
            </div>
          </div>
        </div>

        {/* ── STATS GRID ────────────────────────────────────────────── */}
        <div className="rd-stats">
          {[
            { v: passed, l: 'Passed', c: 'green' },
            { v: partial, l: 'Partial', c: 'gold' },
            { v: failed, l: 'Failed', c: 'red' },
            { v: CHALLENGES.length - attempted, l: 'Skipped', c: 'muted' },
            { v: formatTime(totalTime), l: 'Total Time', c: 'accent' },
            { v: formatTime(avgTime), l: 'Avg / Challenge', c: 'accent' },
          ].map((s, i) => (
            <div key={i} className={`rd-stat rd-stat-${s.c}`}>
              <div className="rd-stat-v">{s.v}</div>
              <div className="rd-stat-l">{s.l}</div>
            </div>
          ))}
        </div>

        {/* ── RANK LADDER ───────────────────────────────────────────── */}
        <div className="rd-card">
          <div className="rd-card-hd">
            <h3>Rank Progression</h3>
            <span className="rd-card-badge">{cuaRank.icon} {cuaRank.rank}</span>
          </div>
          <div className="rd-ladder">
            {RANKS.map(r => {
              const earned = smart >= r.min;
              const current = r.rank === cuaRank.rank;
              return (
                <div key={r.rank} className={`rd-rung rd-rung-${r.cls} ${current ? 'rd-rung-cur' : ''} ${earned ? 'rd-rung-on' : 'rd-rung-off'}`}>
                  <span className="rd-rung-icon">{r.icon}</span>
                  <span className="rd-rung-name">{r.rank}</span>
                  <span className="rd-rung-min">{r.min}+</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── INTELLIGENCE METRICS ──────────────────────────────────── */}
        {hasMetrics && (
          <div className="rd-card">
            <div className="rd-card-hd"><h3>Intelligence Metrics</h3></div>
            <div className="rd-intel">
              {[
                { icon: '🎯', label: 'Accuracy', val: overall.pct ?? 0 },
                { icon: '⚡', label: 'Speed', val: avgSpeed },
                { icon: '🔧', label: 'Efficiency', val: avgEfficiency },
                { icon: '🧠', label: 'Smartness', val: avgSmartness, accent: true },
              ].map((m, i) => {
                const v = m.val ?? 0;
                const g = scoreToGrade(v);
                return (
                  <div key={i} className={`rd-intel-card ${m.accent ? 'rd-intel-accent' : ''}`}>
                    <div className="rd-intel-top">
                      <span className="rd-intel-ic">{m.icon}</span>
                      <span className="rd-intel-lbl">{m.label}</span>
                    </div>
                    <div className={`rd-intel-val rd-c-${g.cls}`}>{m.val != null ? `${m.val}%` : 'N/A'}</div>
                    <div className="rd-bar"><div className={`rd-bar-fill rd-bg-${g.cls}`} style={{ width: `${v}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── TIER BREAKDOWN ────────────────────────────────────────── */}
        <div className="rd-card">
          <div className="rd-card-hd"><h3>Performance by Tier</h3></div>
          <div className="rd-tiers">
            {byTier.map(t => (
              <div key={t.tierName} className={`rd-tier rd-tier-${t.tierName.toLowerCase()}`}>
                <div className="rd-tier-top">
                  <span className="rd-tier-badge">{t.tierName}</span>
                  <span className="rd-tier-pts">{t.earnedPts}/{t.totalPts} pts</span>
                </div>
                <div className="rd-bar"><div className="rd-bar-fill" style={{ width: `${t.pct}%` }} /></div>
                <div className="rd-tier-foot">
                  <span className="rd-tier-pct">{t.pct}%</span>
                  <span>{t.attempted}/{t.total} challenges</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── INSIGHTS ──────────────────────────────────────────────── */}
        <AgentInsights
          results={results}
          resultsWithMetrics={resultsWithMetrics}
          overall={overall}
          avgSpeed={avgSpeed}
          avgEfficiency={avgEfficiency}
          avgSmartness={avgSmartness}
        />

        {/* ── CATEGORY TABLE ────────────────────────────────────────── */}
        <div className="rd-card">
          <div className="rd-card-hd"><h3>Category Performance</h3></div>
          <div className="rd-cat-tbl">
            <div className="rd-cat-row rd-cat-hdr">
              <div className="rd-cat-c1">Category</div>
              <div className="rd-cat-c2">Progress</div>
              <div className="rd-cat-c3">Score</div>
              <div className="rd-cat-c4">Points</div>
              <div className="rd-cat-c5">Grade</div>
            </div>
            {Object.entries(byCategory).map(([cat, data]) => {
              const pct = data.total > 0 ? Math.round((data.earned / data.total) * 100) : 0;
              const g = scoreToGrade(pct);
              return (
                <div key={cat} className="rd-cat-row">
                  <div className="rd-cat-c1">
                    <span className="rd-cat-ic">{CATEGORY_ICONS[cat] || '📋'}</span>
                    <span className="rd-cat-nm">{cat}</span>
                  </div>
                  <div className="rd-cat-c2">
                    <div className="rd-bar"><div className={`rd-bar-fill rd-bg-${g.cls}`} style={{ width: `${pct}%` }} /></div>
                  </div>
                  <div className={`rd-cat-c3 rd-c-${g.cls}`}>{pct}%</div>
                  <div className="rd-cat-c4">{data.earned}/{data.total}</div>
                  <div className="rd-cat-c5"><span className={`rd-pill rd-pill-${g.cls}`}>{g.label}</span></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── CHALLENGE RESULTS TABLE ───────────────────────────────── */}
        <div className="rd-card rd-card-full">
          <div className="rd-card-hd">
            <h3>Challenge Results</h3>
            <span className="rd-card-sub">{attempted}/{CHALLENGES.length} attempted</span>
          </div>
          <div className="rd-ch-tbl">
            <div className="rd-ch-row rd-ch-hdr">
              <div className="rd-ch-c-id">#</div>
              <div className="rd-ch-c-title">Challenge</div>
              <div className="rd-ch-c-diff">Diff</div>
              <div className="rd-ch-c-score">Score</div>
              <div className="rd-ch-c-grade">Grade</div>
              <div className="rd-ch-c-time">Time</div>
              <div className="rd-ch-c-metrics">Metrics</div>
              <div className="rd-ch-c-act"></div>
            </div>
            {CHALLENGES.map(ch => {
              const r = results.find(x => x.challengeId === ch.id);
              const score = r ? r.score : null;
              const g = score != null ? scoreToGrade(score) : null;
              const isOpen = expanded === ch.id;
              const rm = resultsWithMetrics.find(x => x.challengeId === ch.id);
              return (
                <div key={ch.id} className={`rd-ch-item ${score === null ? 'rd-ch-skip' : ''}`}>
                  <div className="rd-ch-row rd-ch-data" onClick={() => r && setExpanded(isOpen ? null : ch.id)}>
                    <div className="rd-ch-c-id"><span className="rd-ch-id">{ch.id}</span></div>
                    <div className="rd-ch-c-title">
                      <div className="rd-ch-name">{ch.title}</div>
                      <div className="rd-ch-cat">{CATEGORY_ICONS[ch.category]} {ch.category} · {ch.points}pts</div>
                    </div>
                    <div className="rd-ch-c-diff">
                      <span className="rd-diff" style={{ color: DIFFICULTY_COLOR[ch.difficulty] }}>{ch.difficulty}</span>
                    </div>
                    <div className="rd-ch-c-score">
                      {score != null ? (
                        <div className="rd-ch-score-wrap">
                          <div className="rd-ch-score-bar"><div className={`rd-bg-${g.cls}`} style={{ width: `${score}%`, height: '100%', borderRadius: 3 }} /></div>
                          <span className={`rd-ch-score-num rd-c-${g.cls}`}>{score}%</span>
                        </div>
                      ) : <span className="rd-ch-dash">—</span>}
                    </div>
                    <div className="rd-ch-c-grade">
                      {g ? <span className={`rd-pill rd-pill-${g.cls}`}>{g.label}</span> : <span className="rd-ch-dash">SKIP</span>}
                    </div>
                    <div className="rd-ch-c-time">
                      {r?.timeSpent != null ? <span className="rd-time">{formatTime(r.timeSpent)}</span> : '—'}
                    </div>
                    <div className="rd-ch-c-metrics">
                      {rm && <>
                        {rm.speedScore != null && <span className="rd-mp rd-mp-spd" title="Speed">⚡{rm.speedScore}</span>}
                        {rm.smartnessScore != null && <span className="rd-mp rd-mp-smt" title="Smartness">🧠{rm.smartnessScore}</span>}
                        {rm.errorAttempts > 0 && <span className="rd-mp rd-mp-err" title="Errors">⚠{rm.errorAttempts}</span>}
                      </>}
                    </div>
                    <div className="rd-ch-c-act">
                      {r ? <span className="rd-ch-exp">{isOpen ? '▲' : '▼'}</span>
                        : <button className="rd-try-btn" onClick={e => { e.stopPropagation(); onRetry(ch.id); }}>Try Now</button>}
                    </div>
                  </div>
                  {isOpen && r && (
                    <div className="rd-ch-detail">
                      {r.feedback.map((fb, i) => (
                        <div key={i} className={`rd-fb rd-fb-${fb.type}`}>{fb.msg}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── VERDICT ───────────────────────────────────────────────── */}
        <div className={`rd-verdict rd-verdict-${overall.grade.cls}`}>
          <div className="rd-verdict-ic">{overall.pct >= 75 ? '🏆' : overall.pct >= 40 ? '📊' : '📉'}</div>
          <div className="rd-verdict-body">
            <div className="rd-verdict-title">
              {overall.pct === 100 ? 'Perfect — Fully Capable'
                : overall.pct >= 75 ? 'Strong — Mostly Capable'
                : overall.pct >= 40 ? 'Moderate — Partial Capabilities'
                : 'Weak — Significant Gaps'}
            </div>
            <div className="rd-verdict-sub">{overall.pct}% · {overall.raw}/{overall.total} pts · {passed}/{CHALLENGES.length} passed</div>
          </div>
          <button className="rd-restart" onClick={onReset}>🔄 Restart Evaluation</button>
        </div>
      </div>
    </div>
  );
}
